import { useState } from 'react';
import axios from 'axios';
import styles from './CustomerClinicRequestForm.module.scss';
import config from '../../config';
import Header from '../../components/Header';
import PatientForm from '@/components/CustomerClinicRequestForm/PatientForm';
import ClinicSelectForm from '@/components/CustomerClinicRequestForm/ClinicSelectForm';
import ExtraInfoForm from '@/components/CustomerClinicRequestForm/ExtraInfoForm';
import { createAccount } from '@/services/data';
import checkEmail from '@/validations/email';

const CustomerClinicRequestPage = () => {
	const [errors, setErrors] = useState('');
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		phoneNumber: '',
		email: '',
		dateOfBirth: '',
		gender: '',
		addressLine1: '',
		city: '',
		county: '',
		postcode: '',
		travel: null as boolean | null,
		accessibilityRequirements: '',
		preferredDatesForTesting: '',
		operativeCheckStatus: '',
		referringClinician: '',
		bloodTestRequired: null as boolean | null,
		badBloodExtractionExperience: null as boolean | null,
		badBloodExtractionExperienceExplanation: '',
		bloodTestTypeRequired: '',
		firstMostSuitableLocation: '',
		firstOtherLocation: '',
		secondMostSuitableLocation: '',
		imagingScanRequired: null as boolean | null,
		medicalDetailForImageScan: '',
		explainedBodyPart: '',
		imageTestTypeRequested: '',
		otherImagingTestRequested: '',
		firstMostPreferredImageTestLocation: '',
		secondMostPreferredImageTestLocation: '',
		heartFitterInstalledMRI: '',
		shuntOrClipInstalledMRI: '',
		implantOrNeurotransmitterMRI: '',
		renalImpairmentMRI: '',
		proceduresSixMonthsMRI: '',
		metallicForeignBodiesEyeMRI: '',
		breastFeedingMRI: '',
		pregnantMRI: '',
		paidProfessionalSport: '',
		complexRequestNordClinic: '',
		lastImageScanDate: '',
		restingECGRequested: null as boolean | null,
		selectedECGLocation: '',
		otherECGLocation: '',
		consentConfirmationECG: null as boolean | null,
		anotherServiceRequired: null as boolean | null,
		requestedServiceDetails: '',
		preferredContactMethod: [] as string[] | null,
		shareResultsNordClinic: null as boolean | null,
		consentImageReportSirkka: null as boolean | null,
		consentMedicalResearch: null as boolean | null,
		isRegisteredSirkkaWebsite: null as boolean | null,
	});

	const [formSubmitSuccessStatus, setFormSubmitSuccessStatus] = useState<boolean | null>(null);
	const [authentication, setAuthentication] = useState({
		password: '',
		confirmPassword: '',
	});
	const [page, setPage] = useState(1);

	const TOTAL_PAGES = 3;

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!validate()) return;

		setErrors('');

		if (formData.isRegisteredSirkkaWebsite === false) {
			try {
				const combinedAddress = `${formData.addressLine1}, ${formData.city}, ${formData.county}, ${formData.postcode}`;
				const response = await createAccount({
					nameTitle: '',
					firstName: formData.firstName,
					lastName: formData.lastName,
					gender: formData.gender,
					dob: formData.dateOfBirth,
					phoneNumber: formData.phoneNumber,
					address: combinedAddress,
					company: '',
					occupation: '',
					email: formData.email,
					password: authentication.password,
					role: 'customer',
				});
				if (response && response.status === 'success') {
					formData.isRegisteredSirkkaWebsite = true; // Account created
				} else {
					setErrors(response.message || 'Unknown error');
				}
			} catch (error: any) {
				setErrors('Could not create account: ' + (error || ''));
			}
		}

		try {
			const mappedFormData = mapNordFormData(formData);
			const response = await axios.post(
				`${config.REACT_APP_BASE_API_URL}/staff/api/v1/crm/ticket/pathology/create`,
				mappedFormData
			);

			if (response.status === 201) {
				setFormSubmitSuccessStatus(true);
			} else {
				setErrors(`Submission failed with status: ${response.status}`);
				setFormSubmitSuccessStatus(false);
			}
		} catch (error: any) {
			setErrors('Could not submit enquiry: ' + (error?.response?.data?.message || error.message));
			setFormSubmitSuccessStatus(false);
		}
	};

	const mapNordFormData = (form: any) => {
		const combinedAddress = `${formData.addressLine1}, ${formData.city}, ${formData.county}, ${formData.postcode}`;
		const data = {
			first_name: form.firstName,
			last_name: form.lastName,
			phone_number: form.phoneNumber,
			email: form.email,
			date_of_birth: form.dateOfBirth,
			gender: form.gender,
			address: combinedAddress,
			referring_clinician: form.referringClinician,
			accessibility_requirements: form.accessibilityRequirements,
			can_travel: form.travel,
			preferred_appt_time: form.preferredDatesForTesting,
			operative_check_status: form.operativeCheckStatus,
			blood_test_required: form.bloodTestRequired,
			blood_draw_bad_experience: form.badBloodExtractionExperience,
			blood_draw_experience_details: form.badBloodExtractionExperienceExplanation,
			service_type: form.otherBloodTestTypeRequired
				? form.otherBloodTestTypeRequired
				: form.bloodTestTypeRequired,
			blood_test_location_primary: form.firstOtherLocation
				? form.firstOtherLocation
				: form.firstMostSuitableLocation,
			blood_test_location_secondary: form.secondMostSuitableLocation,
			imaging_required: form.imagingScanRequired,
			medical_details_for_image_scan: form.medicalDetailForImageScan,
			imaging_body_part_details: form.explainedBodyPart,
			image_test_type_required: form.otherImagingTestRequested
				? form.otherImagingTestRequested
				: form.imageTestTypeRequested,
			imaging_location_preferred: form.firstMostPreferredImageTestLocation,
			imaging_location_preferred_secondary: form.secondMostPreferredImageTestLocation,
			is_heart_fitter_installed: form.heartFitterInstalledMRI,
			is_shunt_or_clip_installed: form.shuntOrClipInstalledMRI,
			is_implant_or_neurotransmitter_installed: form.implantOrNeurotransmitterMRI,
			has_renal_impairment: form.renalImpairmentMRI,
			had_any_surgery_last_six_months: form.proceduresSixMonthsMRI,
			had_metallic_bodies_in_eye: form.metallicForeignBodiesEyeMRI,
			is_breastfeeding: form.breastFeedingMRI,
			is_pregnant: form.pregnantMRI,
			is_involved_in_professional_sports: form.paidProfessionalSport,
			complex_request_details: form.complexRequestNordClinic,
			previous_image_scan_date: form.lastImageScanDate,
			ecg_required: form.restingECGRequested,
			ecg_location_preferred: form.otherECGLocation
				? form.otherECGLocation
				: form.selectedECGLocation,
			share_ecg_result: form.consentConfirmationECG,
			other_service_required: form.anotherServiceRequired,
			other_service_details: form.requestedServiceDetails,
			contact_method_preferred: Array.isArray(form.preferredContactMethod)
				? form.preferredContactMethod.join(', ')
				: form.preferredContactMethod,
			share_results_with_nord: form.shareResultsNordClinic,
			share_imaging_report: form.consentImageReportSirkka,
			anonymised_data_research_consent: form.consentMedicalResearch,
			sirkka_registered: form.isRegisteredSirkkaWebsite,
		};
		return cleanData(data);
	};

	const cleanData = (data: Record<string, any>): Record<string, any> => {
		const cleanedData: Record<string, any> = {};
		Object.keys(data).forEach((key) => {
			const value = data[key];
			if (value !== '' && value !== null && value !== undefined) {
				cleanedData[key] = value;
			}
		});
		return cleanedData;
	};

	const validatePage1 = (formData: any) =>
		formData.firstName &&
		formData.lastName &&
		formData.email &&
		formData.phoneNumber &&
		formData.dateOfBirth &&
		formData.gender &&
		formData.addressLine1 &&
		formData.city &&
		formData.county &&
		formData.postcode &&
		formData.travel !== null &&
		formData.accessibilityRequirements &&
		formData.preferredDatesForTesting &&
		formData.operativeCheckStatus.length > 0;

	const validatePage2 = (formData: any) => {
		const baseValidation =
			formData.bloodTestRequired !== null &&
			formData.imagingScanRequired !== null &&
			formData.restingECGRequested !== null &&
			formData.anotherServiceRequired !== null;
		const areExtraQuestionsRequired =
			formData.imageTestTypeRequested && formData.imageTestTypeRequested !== 'Other';
		if (areExtraQuestionsRequired) {
			return (
				baseValidation &&
				formData.heartFitterInstalledMRI !== '' &&
				formData.shuntOrClipInstalledMRI !== '' &&
				formData.implantOrNeurotransmitterMRI !== '' &&
				formData.renalImpairmentMRI !== '' &&
				formData.proceduresSixMonthsMRI !== '' &&
				formData.metallicForeignBodiesEyeMRI !== '' &&
				formData.breastFeedingMRI !== '' &&
				formData.pregnantMRI !== '' &&
				formData.paidProfessionalSport !== ''
			);
		}
		return baseValidation;
	};

	const validatePage3 = (formData: any, authentication: any) => {
		const baseValid =
			(formData.preferredContactMethod as string[])?.length > 0 &&
			formData.shareResultsNordClinic !== null &&
			formData.consentImageReportSirkka !== null &&
			formData.consentMedicalResearch !== null &&
			formData.isRegisteredSirkkaWebsite !== null;

		if (formData.isRegisteredSirkkaWebsite === false) {
			return (
				baseValid &&
				authentication.password &&
				authentication.confirmPassword &&
				authentication.password === authentication.confirmPassword
			);
		}
		return baseValid;
	};

	const validate = () => {
		let isValid = false;

		if (page === 1) {
			isValid = validatePage1(formData);
			if (!checkEmail(formData.email)) {
				setErrors('Please enter a valid email address.');
				return false;
			}
		} else if (page === 2) {
			isValid = validatePage2(formData);
		} else if (page === 3) {
			isValid = validatePage3(formData, authentication);
			if (
				formData.isRegisteredSirkkaWebsite === false &&
				authentication.password !== authentication.confirmPassword
			) {
				setErrors('Passwords do not match.');
				return false;
			}
		}

		if (!isValid) {
			setErrors('Please fill out all required fields.');
		} else {
			setErrors('');
		}
		return isValid;
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleMultipleCheckbox = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value, type } = e.target;

		if (type === 'checkbox' && name === 'preferredContactMethod') {
			const { checked } = e.target as HTMLInputElement;

			setFormData((prevFormData) => {
				const currentValues = prevFormData.preferredContactMethod || [];
				if (checked) {
					return {
						...prevFormData,
						preferredContactMethod: [...currentValues, value],
					};
				} else {
					return {
						...prevFormData,
						preferredContactMethod: currentValues.filter((v) => v !== value),
					};
				}
			});
		}
	};

	const handleBooleanToggle = (field: keyof typeof formData, value: boolean) => {
		setFormData((prevState) => ({
			...prevState,
			[field]: value,
		}));
	};

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setAuthentication((prev) => ({ ...prev, [name]: value }));
	};

	const handleNextPage = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();

		const isPageValid = validate();
		if (!isPageValid) {
			return;
		} else setErrors('');

		if (page < TOTAL_PAGES) {
			setPage((prev) => prev + 1);
		}
	};

	const handlePrevPage = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();
		if (page > 1) {
			setPage((prev) => prev - 1);
		}
	};

	if (formSubmitSuccessStatus) {
		return (
			<>
				<div className={styles['contact-form-container']}>
					<h1 className={styles['form-title']}>Thank you for your submission!</h1>
					<p className="text-center">
						We've received your enquiry and will get back to you shortly.
					</p>
					<p className="text-center">
						If you have any further questions, feel free to email us at{' '}
						<a href="mailto:customers@sirkkaltd.com">
							<u>customers@sirkkaltd.com</u>
						</a>
						.
					</p>
				</div>
			</>
		);
	}

	return (
		<>
			<Header title="Customer Test Enquiry Form" fullTitle="Customer Test Enquiry Form" />
			<div className={styles['contact-form-container']}>
				<h1 className={styles['form-title']}>Customer Clinic enquiry form</h1>
				<p className="text-center">
					Please complete this form with as much detail as you can. If you have any issues, concerns
					or feedback, please contact us via email at{' '}
					<a href="mailto:customers@sirkkaltd.com">
						<u>customers@sirkkaltd.com</u>
					</a>
					.
				</p>

				<form onSubmit={handleSubmit} className={styles['contact-form']}>
					{page === 1 && (
						<PatientForm
							formData={formData}
							handleChange={handleChange}
							handleBooleanToggle={handleBooleanToggle}
						/>
					)}
					{page === 2 && (
						<ClinicSelectForm
							formData={formData}
							handleChange={handleChange}
							handleBooleanToggle={handleBooleanToggle}
							setFormData={setFormData}
						/>
					)}
					{page === 3 && (
						<ExtraInfoForm
							formData={formData}
							handleMultipleCheckbox={handleMultipleCheckbox}
							handleBooleanToggle={handleBooleanToggle}
							authentication={authentication}
							handlePasswordChange={handlePasswordChange}
						/>
					)}

					{errors && (
						<div className={styles['error-message']}>
							<span>{errors}</span>
						</div>
					)}

					<div className={styles['flex-container']}>
						<button
							className="primary_btn"
							disabled={page === 1}
							onClick={handlePrevPage}
							type="button"
						>
							Back
						</button>

						{page < TOTAL_PAGES ? (
							<button className="primary_btn" type="button" onClick={handleNextPage}>
								Next
							</button>
						) : (
							<button className="primary_btn" type="submit">
								Submit
							</button>
						)}
					</div>
				</form>
			</div>
		</>
	);
};

export default CustomerClinicRequestPage;
